import { useState, useMemo, useEffect } from 'react'
import {
  BookOpen,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Sparkles,
  Bot,
  User,
  Tag,
  FolderTree,
  Filter,
  X,
  Check,
  AlertCircle,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Progress } from '@/components/ui/progress'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useSynonyms,
  useCreateSynonym,
  useUpdateSynonym,
  useDeleteSynonym,
  useGenerateSynonyms,
  useGenerationStatus,
  useLatestGenerationLog,
  useDeleteAISynonyms,
  useGroupNames,
  type SynonymGroup,
  type SynonymOriginType,
  type GenerationLog,
} from '../hooks/useSynonyms'

// ============================================================
// Utility Functions
// ============================================================

/**
 * Format timestamp to relative time
 */
function formatRelativeTime(timestamp: number): string {
  const now = Date.now()
  const diff = now - timestamp * 1000 // Convert seconds to ms

  if (diff < 60000) return 'just now'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
  return `${Math.floor(diff / 86400000)}d ago`
}

/**
 * Get origin icon component
 */
function OriginIcon({ type }: { type: SynonymOriginType }) {
  switch (type) {
    case 'manual':
      return <User className="h-3.5 w-3.5" />
    case 'ai_product':
      return <Tag className="h-3.5 w-3.5" />
    case 'ai_category':
      return <FolderTree className="h-3.5 w-3.5" />
    case 'ai_general':
      return <Bot className="h-3.5 w-3.5" />
    default:
      return <User className="h-3.5 w-3.5" />
  }
}

/**
 * Get origin label
 */
function getOriginLabel(type: SynonymOriginType): string {
  switch (type) {
    case 'manual':
      return 'Manual'
    case 'ai_product':
      return 'AI (Product)'
    case 'ai_category':
      return 'AI (Category)'
    case 'ai_general':
      return 'AI (General)'
    default:
      return 'Manual'
  }
}

// ============================================================
// Synonym Form Component
// ============================================================

function SynonymForm({
  synonym,
  onSubmit,
  onCancel,
  isLoading,
}: {
  synonym?: SynonymGroup
  onSubmit: (data: { term: string; synonyms: string[] }) => void
  onCancel: () => void
  isLoading: boolean
}) {
  const [term, setTerm] = useState(synonym?.term || '')
  const [synonymsText, setSynonymsText] = useState(synonym?.synonyms.join(', ') || '')
  const [error, setError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const trimmedTerm = term.trim()
    if (!trimmedTerm) {
      setError('Term is required')
      return
    }

    const synonymsList = synonymsText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)

    if (synonymsList.length === 0) {
      setError('At least one synonym is required')
      return
    }

    onSubmit({ term: trimmedTerm, synonyms: synonymsList })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="term">Base Term</Label>
        <Input
          id="term"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="e.g., sofa"
          disabled={isLoading}
        />
        <p className="text-xs text-muted-foreground">
          The canonical term for this group
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="synonyms">Synonyms</Label>
        <Input
          id="synonyms"
          value={synonymsText}
          onChange={(e) => setSynonymsText(e.target.value)}
          placeholder="e.g., couch, settee, divan"
          disabled={isLoading}
        />
        <p className="text-xs text-muted-foreground">
          Comma-separated list of alternative terms
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {synonym ? 'Update' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  )
}

// ============================================================
// Generation Progress Modal
// ============================================================

function GenerationProgressModal({
  open,
  onOpenChange,
  log,
  totalProducts,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  log: GenerationLog | null
  totalProducts: number
}) {
  const progress = useMemo(() => {
    if (!log || !totalProducts || log.status !== 'running') return 0
    return Math.min(100, Math.round((log.productsAnalyzed / totalProducts) * 100))
  }, [log, totalProducts])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Generating Synonyms with AI
          </DialogTitle>
          <DialogDescription>
            Analyzing your products to find synonym opportunities
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Stats */}
          {log && (
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border p-3">
                <div className="text-2xl font-bold">{log.productsAnalyzed}</div>
                <div className="text-xs text-muted-foreground">Products Analyzed</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-2xl font-bold">{log.synonymsGenerated}</div>
                <div className="text-xs text-muted-foreground">Synonyms Found</div>
              </div>
            </div>
          )}

          {/* Status */}
          {log?.status === 'running' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing batch {Math.ceil((log.productsAnalyzed || 1) / 20)} of {Math.ceil(totalProducts / 20)}...
            </div>
          )}

          {log?.status === 'complete' && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <Check className="h-4 w-4" />
              Generation complete! Found {log.synonymsGenerated} new synonyms, merged {log.synonymsMerged} existing.
            </div>
          )}

          {log?.status === 'error' && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {log.error || 'Generation failed'}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant={log?.status === 'running' ? 'outline' : 'default'}
            onClick={() => onOpenChange(false)}
          >
            {log?.status === 'running' ? 'Run in Background' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================
// Synonym Row Component (Updated with Origin Info)
// ============================================================

function SynonymRow({
  synonym,
  onEdit,
  onToggle,
  onDelete,
}: {
  synonym: SynonymGroup
  onEdit: () => void
  onToggle: (enabled: boolean) => void
  onDelete: () => void
}) {
  const deleteMutation = useDeleteSynonym()
  const updateMutation = useUpdateSynonym()
  const isAI = synonym.originType !== 'manual'

  return (
    <TableRow className={!synonym.enabled ? 'opacity-60' : ''}>
      {/* Term */}
      <TableCell>
        <div className="font-medium">{synonym.term}</div>
        {synonym.groupName && (
          <Badge variant="outline" className="mt-1 text-xs">
            {synonym.groupName}
          </Badge>
        )}
      </TableCell>

      {/* Synonyms */}
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {synonym.synonyms.map((s, i) => (
            <Badge key={i} variant="secondary">
              {s}
            </Badge>
          ))}
        </div>
      </TableCell>

      {/* Origin */}
      <TableCell>
        <div className="flex items-center gap-1.5">
          <OriginIcon type={synonym.originType} />
          <span className="text-sm text-muted-foreground">
            {getOriginLabel(synonym.originType)}
          </span>
        </div>
        {isAI && synonym.originName && (
          <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[150px]" title={synonym.originName}>
            {synonym.originName}
          </div>
        )}
        {/* Confidence indicator */}
        {isAI && synonym.confidence !== null && (
          <div className="flex items-center gap-1 mt-1">
            <div className="h-1.5 w-16 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full"
                style={{ width: `${synonym.confidence * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {Math.round(synonym.confidence * 100)}%
            </span>
          </div>
        )}
      </TableCell>

      {/* Enabled */}
      <TableCell>
        <Switch
          checked={synonym.enabled}
          onCheckedChange={(checked) => onToggle(checked)}
          disabled={updateMutation.isPending}
        />
      </TableCell>

      {/* Actions */}
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="icon" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon">
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Synonym Group</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete the synonym group for "{synonym.term}"?
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Delete'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
  )
}

// ============================================================
// Synonyms Page
// ============================================================

export function SynonymsPage() {
  const { data: synonyms, isLoading, error } = useSynonyms()
  const { data: groupNames } = useGroupNames()
  const { data: latestLog, refetch: refetchLatestLog } = useLatestGenerationLog()

  const createMutation = useCreateSynonym()
  const updateMutation = useUpdateSynonym()
  const deleteMutation = useDeleteSynonym()
  const generateMutation = useGenerateSynonyms()
  const deleteAIMutation = useDeleteAISynonyms()

  // State
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingSynonym, setEditingSynonym] = useState<SynonymGroup | undefined>()
  const [progressModalOpen, setProgressModalOpen] = useState(false)
  const [currentLogId, setCurrentLogId] = useState<string | null>(null)

  // Filters
  const [originFilter, setOriginFilter] = useState<string>('all')
  const [groupFilter, setGroupFilter] = useState<string>('all')

  // Poll generation status
  const { data: currentLog } = useGenerationStatus(currentLogId, progressModalOpen)

  // Close modal when generation completes
  useEffect(() => {
    if (currentLog?.status === 'complete' || currentLog?.status === 'error') {
      // Refetch synonyms and latest log
      refetchLatestLog()
    }
  }, [currentLog?.status, refetchLatestLog])

  // Calculate total products (estimate based on synonyms generated)
  const estimatedProducts = latestLog?.productsAnalyzed || 100

  // Filter synonyms
  const filteredSynonyms = useMemo(() => {
    if (!synonyms) return []

    return synonyms.filter((s) => {
      // Origin filter
      if (originFilter === 'manual' && s.originType !== 'manual') return false
      if (originFilter === 'ai' && s.originType === 'manual') return false

      // Group filter
      if (groupFilter !== 'all' && s.groupName !== groupFilter) return false

      return true
    })
  }, [synonyms, originFilter, groupFilter])

  // Count AI vs manual synonyms
  const counts = useMemo(() => {
    if (!synonyms) return { total: 0, manual: 0, ai: 0 }
    const manual = synonyms.filter((s) => s.originType === 'manual').length
    return { total: synonyms.length, manual, ai: synonyms.length - manual }
  }, [synonyms])

  // Handlers
  const handleCreate = async (data: { term: string; synonyms: string[] }) => {
    try {
      await createMutation.mutateAsync(data)
      setDialogOpen(false)
    } catch {
      // Error handled by mutation
    }
  }

  const handleUpdate = async (data: { term: string; synonyms: string[] }) => {
    if (!editingSynonym) return
    try {
      await updateMutation.mutateAsync({ id: editingSynonym.id, data })
      setEditingSynonym(undefined)
      setDialogOpen(false)
    } catch {
      // Error handled by mutation
    }
  }

  const handleToggle = async (synonym: SynonymGroup, enabled: boolean) => {
    try {
      await updateMutation.mutateAsync({ id: synonym.id, data: { enabled } })
    } catch {
      // Error handled by mutation
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id)
    } catch {
      // Error handled by mutation
    }
  }

  const handleGenerate = async () => {
    try {
      const result = await generateMutation.mutateAsync()
      setCurrentLogId(result.logId)
      setProgressModalOpen(true)
    } catch {
      // Error handled by mutation
    }
  }

  const handleDeleteAllAI = async () => {
    try {
      await deleteAIMutation.mutateAsync()
    } catch {
      // Error handled by mutation
    }
  }

  const openEditDialog = (synonym: SynonymGroup) => {
    setEditingSynonym(synonym)
    setDialogOpen(true)
  }

  const openCreateDialog = () => {
    setEditingSynonym(undefined)
    setDialogOpen(true)
  }

  const clearFilters = () => {
    setOriginFilter('all')
    setGroupFilter('all')
  }

  const hasActiveFilters = originFilter !== 'all' || groupFilter !== 'all'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to load synonyms</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Synonyms
          </h1>
          <p className="text-muted-foreground mt-1">
            Map alternative terms so "couch" finds "sofa" and vice versa
          </p>
        </div>
        <div className="flex gap-2">
          {/* Generate with AI Button */}
          <Button
            variant="outline"
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Generate with AI
          </Button>

          {/* Add Synonym Group Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Add Synonym Group
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingSynonym ? 'Edit' : 'Create'} Synonym Group</DialogTitle>
                <DialogDescription>
                  {editingSynonym
                    ? 'Update the synonym group settings'
                    : 'Create a new group of synonyms that should match each other'}
                </DialogDescription>
              </DialogHeader>
              <SynonymForm
                synonym={editingSynonym}
                onSubmit={editingSynonym ? handleUpdate : handleCreate}
                onCancel={() => setDialogOpen(false)}
                isLoading={createMutation.isPending || updateMutation.isPending}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Last Generated Info */}
      {latestLog && latestLog.status === 'complete' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4" />
          <span>
            Last AI generation: {formatRelativeTime(latestLog.completedAt || latestLog.startedAt)}
            ({latestLog.synonymsGenerated} created, {latestLog.synonymsMerged} merged)
          </span>
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="pt-4">
          <div className="flex gap-4 text-sm">
            <div className="flex-1">
              <p className="font-medium mb-1">How synonyms work</p>
              <p className="text-muted-foreground">
                When a user searches for any word in a synonym group, results for all words in
                that group will be included. For example, if you create a group with "sofa" as the
                term and "couch, settee" as synonyms, searching for "couch" will also show "sofa" products.
              </p>
            </div>
            <div className="flex-1">
              <p className="font-medium mb-1">AI Generation</p>
              <p className="text-muted-foreground">
                Click "Generate with AI" to automatically analyze your products and create synonym
                suggestions. AI-generated synonyms are auto-enabled but you can review, edit, or
                disable them at any time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Synonyms Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Synonym Groups</CardTitle>
              <CardDescription>
                {counts.total} synonym group{counts.total !== 1 ? 's' : ''}
                ({counts.manual} manual, {counts.ai} AI-generated)
              </CardDescription>
            </div>

            {/* Delete All AI Button */}
            {counts.ai > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete AI Synonyms
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete All AI Synonyms</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will delete all {counts.ai} AI-generated synonym groups.
                      Manual synonyms will not be affected. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAllAI}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteAIMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Delete All AI Synonyms'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>

          {/* Filters */}
          {synonyms && synonyms.length > 0 && (
            <div className="flex items-center gap-4 pt-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Filter:</span>
              </div>

              {/* Origin Filter */}
              <Select value={originFilter} onValueChange={setOriginFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Origin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Origins</SelectItem>
                  <SelectItem value="manual">Manual Only</SelectItem>
                  <SelectItem value="ai">AI Generated</SelectItem>
                </SelectContent>
              </Select>

              {/* Group Filter */}
              {groupNames && groupNames.length > 0 && (
                <Select value={groupFilter} onValueChange={setGroupFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Groups</SelectItem>
                    {groupNames.map((group) => (
                      <SelectItem key={group} value={group}>
                        {group}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="mr-1 h-3 w-3" />
                  Clear
                </Button>
              )}

              {/* Results count */}
              {hasActiveFilters && (
                <span className="text-sm text-muted-foreground">
                  Showing {filteredSynonyms.length} of {synonyms.length}
                </span>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!synonyms || synonyms.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No synonyms configured</p>
              <p className="text-sm text-muted-foreground">
                Add synonym groups manually or generate them with AI
              </p>
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" onClick={handleGenerate} disabled={generateMutation.isPending}>
                  {generateMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  Generate with AI
                </Button>
                <Button onClick={openCreateDialog}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Manually
                </Button>
              </div>
            </div>
          ) : filteredSynonyms.length === 0 ? (
            <div className="text-center py-8">
              <Filter className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No synonyms match your filters</p>
              <Button variant="ghost" onClick={clearFilters} className="mt-2">
                Clear Filters
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Base Term</TableHead>
                  <TableHead>Synonyms</TableHead>
                  <TableHead>Origin</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSynonyms.map((synonym) => (
                  <SynonymRow
                    key={synonym.id}
                    synonym={synonym}
                    onEdit={() => openEditDialog(synonym)}
                    onToggle={(enabled) => handleToggle(synonym, enabled)}
                    onDelete={() => handleDelete(synonym.id)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Example Queries */}
      {synonyms && synonyms.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Test Your Synonyms</CardTitle>
            <CardDescription>
              Try these example searches to see synonyms in action
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {synonyms.slice(0, 5).map((s) => (
                <Badge key={s.id} variant="outline" className="text-sm">
                  Search: "{s.term}" or "{s.synonyms[0]}"
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generation Progress Modal */}
      <GenerationProgressModal
        open={progressModalOpen}
        onOpenChange={setProgressModalOpen}
        log={currentLog || null}
        totalProducts={estimatedProducts}
      />
    </div>
  )
}
